import {
  createTemplateShapeName,
  type FormConfig,
  getGeneratorVersion,
  getServiceIdFieldName,
  getServiceObjectFields,
  getShapeQueriesAndMutations,
  type PatternGenerator,
  type PropertySchema,
  type QueryJSON,
  type QueryMap,
  SERVICE_OBJECT_PATTERN_NAME,
  type ServiceInfo,
  type ShapeJSON,
  type ShapeWithObjectSchema
} from '@takeshape/schema';
import { pascalCase } from '@takeshape/util';
import { produce } from 'immer';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import upperFirst from 'lodash/upperFirst';
import type { SchemaUpdate } from '../../branches/api.js';
import type { ProjectSchemaCompat } from '../../compat/types.js';
import shopifyCollection from './service-widget-shapes/shopify-collection.json';
import shopifyCollectionInput from './service-widget-shapes/shopify-collection-input.json';
import shopifyProduct from './service-widget-shapes/shopify-product.json';
import shopifyProductInput from './service-widget-shapes/shopify-product-input.json';
import shopifyProductVariant from './service-widget-shapes/shopify-product-variant.json';
import shopifyProductVariantInput from './service-widget-shapes/shopify-product-variant-input.json';

/**
 * Get the form property for a service pattern resolver property.
 */
export function getServiceFormProperty(provider: string, serviceObjectType: string): FormConfig {
  return {
    properties: getServiceFormPropertyChildren(provider, serviceObjectType),
    widget: provider,
    wrapper: `${provider}ServiceWrapper`,
    order: getServiceFormOrder(provider, serviceObjectType)
  };
}

/**
 * Get the form property for a service pattern ID property.
 */
export function getServiceIdFormProperty(provider: string, service: string, serviceObjectType: string): FormConfig {
  if (provider === 'shopify') {
    return {
      instructions: `Format: gid://shopify/${pascalCase(serviceObjectType)}/1111111111111`,
      label: `${serviceObjectType} ID`,
      widget: 'serviceObjectId',
      provider,
      serviceObjectType,
      service
    };
  }

  throw new Error(`getServiceIdFormProperty not implemented for provider "${provider}"`);
}

/**
 * Get the form order for properties of a service pattern resolver property.
 */
export function getServiceFormOrder(provider: string, objectType: string): string[] {
  // Based on the service, specify order for some or all properties
  let result: string[];
  if (provider === 'shopify') {
    if (objectType === 'product') {
      result = [
        'title',
        'handle',
        'descriptionHtml',
        'bodyHtml',
        'tags',
        'vendor',
        'publishedAt',
        'variants',
        'images'
      ];
    } else if (objectType === 'productVariant') {
      result = ['title', 'productId', 'options', 'barcode', 'sku', 'price', 'compareAtPrice', 'image'];
    } else if (objectType === 'collection') {
      result = ['title', 'handle', 'descriptionHtml', 'products', 'image'];
    } else {
      result = [];
    }
  } else {
    result = [];
  }

  // Append remaining properties in whatever order
  const allProps = Object.keys(getServiceFormPropertyChildren(provider, objectType));
  allProps.forEach((prop) => {
    if (!result.includes(prop)) {
      result.push(prop);
    }
  });
  return result;
}

/**
 * Get the child form properties of a service pattern resolver field.
 * @see getServiceFormProperty
 */
export function getServiceFormPropertyChildren(provider: string, objectType: string): Record<string, FormConfig> {
  let inputType: any;
  let omittedFields = ['id'];
  if (provider === 'shopify') {
    if (objectType === 'productVariant') {
      inputType = shopifyProductVariantInput;

      // This isn't on the input type but we want to see it
      inputType.schema.properties.image = {
        description: 'The image associated with the collection.'
      };

      omittedFields = omittedFields.concat([
        'inventoryItem',
        'metafields',
        'privateMetafields',
        'inventoryPolicy',
        'harmonizedSystemCode', // Deprecated
        'requiresShipping', // Deprecated
        'inventoryManagement', // Deprecated
        'imageId',
        'imageSrc',
        'inventoryQuantities'
      ]);
    } else if (objectType === 'product') {
      inputType = shopifyProductInput;
      omittedFields = omittedFields.concat([
        'privateMetafields',
        'productPublications',
        'publications',
        'options',
        'seo',
        'metafields',
        'collections',
        'collectionsToJoin',
        'collectionsToLeave',
        'publishDate',
        'publishOn',
        'published'
      ]);
    } else if (objectType === 'collection') {
      inputType = shopifyCollectionInput;
      omittedFields = omittedFields.concat(['publications', 'privateMetafields', 'ruleSet', 'metafields', 'seo']);
    }
  }

  if (inputType === undefined) {
    throw new Error(
      `getServiceFormPropertyChildren not implemented for provider "${provider}", objectType "${objectType}"`
    );
  }

  const allProps = Object.keys(inputType.schema.properties).filter((prop) => !omittedFields.includes(prop));

  const result: Record<string, FormConfig> = {};

  allProps.forEach((prop) => {
    let widget;
    if (provider === 'shopify' && objectType === 'collection' && prop === 'products') {
      widget = 'shopifyRelationshipSummary';
    } else if (provider === 'shopify' && objectType === 'collection' && prop === 'image') {
      widget = 'shopifyCollectionImage';
    } else if (provider === 'shopify' && objectType === 'productVariant' && prop === 'image') {
      widget = 'shopifyCollectionImage';
    } else if (provider === 'shopify' && objectType === 'product' && prop === 'variants') {
      widget = 'shopifyRelationshipSummary';
    } else if (provider === 'shopify' && objectType === 'product' && prop === 'images') {
      widget = 'shopifyProductImages';
    } else {
      widget = 'serviceObjectProperty';
    }

    result[prop] = {
      widget,
      provider
    };
  });

  return result;
}

/**
 * Get all remote service Shapes for a provider. This is not resolved, this uses
 * a "cached" copy in the codebase.
 */
export function getProviderShapes(provider: string): Record<string, ShapeWithObjectSchema> {
  if (provider === 'shopify') {
    return {
      product: { ...shopifyProduct } as ShapeWithObjectSchema,

      productVariant: { ...shopifyProductVariant } as ShapeWithObjectSchema,

      collection: { ...shopifyCollection } as ShapeWithObjectSchema
    };
  }

  throw new Error(`getProviderShapes not implemented for provider "${provider}"`);
}

/**
 * Get a remote service Shape. This is not resolved, this uses a "cached" copy in the codebase.
 */
export function getProviderShape(provider: string, objectType: string): ShapeWithObjectSchema {
  return getProviderShapes(provider)[objectType];
}

/**
 * These fields are not included in the input type and are shown as read-only in the UI
 */
const readOnlyServiceProperties: Record<string, Record<string, Record<string, boolean>>> = {
  shopify: {
    product: {
      publishedAt: true,
      variants: true,
      images: true
    },
    productVariant: {
      // The title cannot be edited because it is generated from the variant options
      title: true,
      image: true
    },
    collection: {
      products: true,
      seo: true,
      image: true
    }
  }
};

/**
 * Get the schema of a service pattern id property. It expects an accompanying service pattern resolver property.
 * @see getServiceFieldSchema
 */

export function getServiceIdFieldSchema(
  propertyName: string,
  shapeName: string,
  provider: string,
  serviceObjectType: string,
  title?: string
): PropertySchema {
  if (title === undefined) {
    title = `${provider} ${serviceObjectType}`;
  }

  const capitalizedObjectType = upperFirst(serviceObjectType);
  return {
    type: 'string',
    '@mapping': `shapedb:${shapeName}.${propertyName}`,
    title,
    minLength: 1,
    pattern: `(^gid://${provider}/${capitalizedObjectType}/\\d+$)`
  };
}

/**
 * Get the schema of a service pattern resolver property. It expects an accompanying service pattern ID property.
 * @see getServiceIdFieldSchema
 */
export function getServiceFieldSchema(
  service: string,
  provider: string,
  objectType: string,
  idFieldName: string
): PropertySchema {
  if (provider === 'shopify') {
    // EG 'Product'
    const refShapeName = `${startCase(objectType).replace(/ /g, '')}`;

    const shopifyProperty: PropertySchema = {
      '@ref': `${service}:${refShapeName}`,
      '@resolver': {
        name: 'graphql:query',
        service,
        fieldName: objectType,
        args: {
          ops: [
            {
              path: 'id',
              mapping: `$source.${idFieldName}`
            }
          ]
        }
      },
      '@tag': `${SERVICE_OBJECT_PATTERN_NAME}:${getGeneratorVersion(SERVICE_OBJECT_PATTERN_NAME)}`,
      '@input': { '@ref': `${service}:${refShapeName}Input` }
    };
    return shopifyProperty;
  }

  throw new Error(`getServiceFieldSchema not implemented for provider "${provider}"`);
}

/**
 * Get the create mutation for a Shape with a Shopify service pattern on it. This replaces
 * the default create mutation for that Shape.
 */
function getShopifyCreateMutation(
  shape: ShapeJSON,
  serviceInfo: ServiceInfo,
  patternGenerator: PatternGenerator
): QueryJSON {
  const { name } = shape;
  const { service, fieldName, path } = serviceInfo;
  const { idFieldName, serviceObjectType } = patternGenerator;
  const fieldPath = path.length ? `${path.join('.')}.` : '';

  const serviceArgsOps = getWriteableProperties('shopify', serviceObjectType, true).map((propName) => ({
    path: `input.${propName}`,
    mapping: `$args.input.${fieldPath}${fieldName}.${propName}`
  }));

  return {
    shape: createTemplateShapeName('CreateResult', name),
    resolver: {
      compose: [
        {
          id: 'shopifyCreate',
          if: `!isEmpty(args.input.${fieldPath}${fieldName})`,
          name: 'graphql:mutation',
          service,
          fieldName: `${serviceObjectType}Create`,
          options: {
            selectionSet: `{ ${serviceObjectType} { id } }`
          },
          args: {
            ops: serviceArgsOps
          }
        },
        {
          id: 'creatingShopifyObject',
          if: `!isEmpty(args.input.${fieldPath}${fieldName})`,
          name: 'shapedb:create',
          service: 'shapedb',
          shapeName: name,
          args: {
            ops: [
              {
                path: 'input',
                mapping: '$args.input'
              },
              {
                path: `input.${fieldPath}${idFieldName}`,
                mapping: `$resolvers.shopifyCreate.${serviceObjectType}.id`
              }
            ]
          }
        },
        {
          id: 'notCreatingShopifyObject',
          if: `isEmpty(args.input.${fieldPath}${fieldName})`,
          name: 'shapedb:create',
          service: 'shapedb',
          shapeName: name,
          args: {
            ops: [
              {
                path: 'input',
                mapping: '$args.input'
              }
            ]
          }
        }
      ],
      results: {
        ops: [
          {
            path: 'result',
            mapping: [
              ['get', { path: '$resolvers.creatingShopifyObject.result' }],
              ['get', { path: '$resolvers.notCreatingShopifyObject.result' }]
            ]
          }
        ]
      }
    },
    description: `Create ${name}. If Shopify values are provided, a Shopify ${serviceObjectType} is also created and the new ${serviceObjectType} ID is saved.`,
    args: createTemplateShapeName('CreateArgs', name)
  };
}

/**
 * Get the properties of a service object that should be considered writeable for the purposes of the
 * form studio. This can differ for creates and updates. This list is hand-picked and is subject to the
 * service's domain rules.
 */
export function getWriteableProperties(provider: string, serviceObjectType: string, forCreate = false): string[] {
  return Object.keys(getServiceFormPropertyChildren(provider, serviceObjectType)).filter((propertyName: string) => {
    if (readOnlyServiceProperties[provider]?.[serviceObjectType]?.[propertyName]) {
      return false;
    }

    if (!forCreate && provider === 'shopify' && serviceObjectType === 'productVariant') {
      return propertyName !== 'productId'; // Cannot update productId, shopify api will error if you try
    }

    return true;
  });
}

/**
 * Get the update mutation for a Shape with a Shopify service pattern on it. This replaces
 * the default update mutation for that Shape.
 */
export function getShopifyUpdateMutation(
  shape: ShapeJSON,
  serviceInfo: ServiceInfo,
  patternGenerator: PatternGenerator
): QueryJSON {
  const { name } = shape;
  const { service, fieldName, path } = serviceInfo;
  const { idFieldName, serviceObjectType } = patternGenerator;

  const fieldPath = path.length > 0 ? `.${path.join('.')}` : '';
  const fieldLocation = `$args.input${fieldPath}`;

  const serviceArgsOps = getWriteableProperties('shopify', serviceObjectType, true).map((propName) => ({
    path: `input.${propName}`,
    mapping: `${fieldLocation}.${fieldName}.${propName}`
  }));

  return {
    shape: createTemplateShapeName('UpdateResult', name),
    resolver: {
      compose: [
        {
          id: 'createNewShopifyObject',
          if: `isEmpty(args.input${fieldPath}.${idFieldName}) && !isEmpty(args.input${fieldPath}.${fieldName})`,
          name: 'graphql:mutation',
          service,
          fieldName: `${serviceObjectType}Create`,
          options: {
            selectionSet: `{ ${serviceObjectType} { id } }`
          },
          args: {
            ops: serviceArgsOps
          }
        },
        {
          id: 'editTakeshape',
          name: 'shapedb:update',
          service: 'shapedb',
          shapeName: name,
          args: {
            ops: [
              {
                path: 'input',
                mapping: '$args.input'
              },
              {
                path: `input${fieldPath}.${idFieldName}`,
                mapping: [
                  [
                    'get',
                    {
                      path: `$resolvers.createNewShopifyObject.${serviceObjectType}.id`
                    }
                  ],
                  ['get', { path: `$args.input${fieldPath}.${idFieldName}` }]
                ]
              }
            ]
          }
        },
        {
          id: 'updateExistingShopifyObject',
          if: `!isEmpty(args.input${fieldPath}.${idFieldName}) && !isEmpty(args.input${fieldPath}.${fieldName})`,
          name: 'graphql:mutation',
          service,
          fieldName: `${serviceObjectType}Update`,
          options: {
            selectionSet: `{ ${serviceObjectType} { id } }`
          },
          args: {
            ops: [
              ...serviceArgsOps,
              {
                path: 'input.id',
                mapping: `$args.input${fieldPath}.${idFieldName}`
              }
            ]
          }
        }
      ],
      results: {
        ops: [
          {
            path: 'result',
            mapping: '$resolvers.editTakeshape.result'
          }
        ]
      }
    },
    description: `Update ${name}. If the input has Shopify values and a Shopify ID, the Shopify ${serviceObjectType} with that ID is updated.
If the input has Shopify values and no Shopify ID, a Shopify ${serviceObjectType} is created.`,
    args: createTemplateShapeName('UpdateArgs', name)
  };
}

export function getShopifyQueriesAndMutations(
  shape: ShapeJSON,
  serviceInfo: ServiceInfo,
  generator: PatternGenerator
): { queries: QueryMap; mutations: QueryMap } {
  const { name } = shape;

  const result = getShapeQueriesAndMutations([shape]);

  // Override update
  result.mutations[`update${name}`] = getShopifyUpdateMutation(shape, serviceInfo, generator);

  // Override create
  result.mutations[`create${name}`] = getShopifyCreateMutation(shape, serviceInfo, generator);

  return result;
}

/**
 * Create a SchemaUpdate for adding a service pattern to a shape.
 * If shape doesn't exist it is created.
 * This involves multiple schema changes:
 * * Add service resolver property to the Shape
 * * Add service ID property to the Shape
 * * Overwrite the queries and mutations for the Shape
 */

export function getServicePatternSchemaUpdate(
  projectSchema: ProjectSchemaCompat,
  shape: ShapeWithObjectSchema,
  form: FormConfig,
  service: string,
  provider: string,
  serviceObjectType: string
): SchemaUpdate {
  if (provider === 'shopify') {
    if (projectSchema.forms === undefined) {
      projectSchema.forms = {};
    }

    const { properties } = shape.schema;
    let servicePropName: string | undefined;
    let servicePropIdName: string | undefined;
    let servicePropNameAttempt = 1;
    while (servicePropName === undefined || servicePropIdName === undefined) {
      const potentialName = camelCase(
        `${provider} ${serviceObjectType}${servicePropNameAttempt === 1 ? '' : servicePropNameAttempt}`
      );
      const potentialIdName = getServiceIdFieldName(potentialName);
      if (shape.schema.properties[potentialName] || shape.schema.properties[potentialIdName]) {
        servicePropNameAttempt++;
      } else {
        servicePropName = potentialName;
        servicePropIdName = potentialIdName;
      }
    }

    const updatedShape = {
      ...shape,
      schema: {
        ...shape.schema,
        properties: {
          ...properties,
          [servicePropName]: getServiceFieldSchema(service, provider, serviceObjectType, servicePropIdName),
          [servicePropIdName]: getServiceIdFieldSchema(servicePropIdName, shape.name, provider, serviceObjectType)
        }
      }
    };

    const serviceObjectFields = getServiceObjectFields(projectSchema, updatedShape, provider);

    if (serviceObjectFields.length === 0) {
      throw new Error('Could not get service info in getServicePatternSchemaUpdate');
    }

    if (serviceObjectFields.length > 1) {
      throw new Error(`Shapes with more than one ${SERVICE_OBJECT_PATTERN_NAME} tagged field are not supported`);
    }

    const serviceInfo = serviceObjectFields[0];
    const generator = serviceInfo.generators[SERVICE_OBJECT_PATTERN_NAME];

    const newForm = produce(form, (draft) => {
      draft.properties[servicePropName!] = getServiceFormProperty(provider, serviceObjectType);
      draft.properties[servicePropIdName!] = getServiceIdFormProperty(provider, service, serviceObjectType);
      draft.order = draft.order.concat([servicePropIdName, servicePropName]);
    });

    return {
      version: projectSchema.version,
      ...getShopifyQueriesAndMutations(shape, serviceInfo, generator),
      shapes: {
        [shape.name]: updatedShape
      },
      forms: {
        [shape.name]: {
          default: newForm
        }
      }
    };
  }

  throw new Error(`getServicePatternSchemaUpdate not implemented for provider "${provider}"`);
}
